import React from 'react';
import { useIntl } from 'umi';
import { getOptionText } from '@intelligence/components/Form/components/common/Cascader';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getCascaderDataSourceItem } from './platformCascaderDisplayUtils';

const SHORTCUT_ICON_MAP: Record<string, string> = {
  PC: 'pc',
  PlayStation: 'playstation',
  Xbox: 'xbox',
  Nintendo: 'nintendo',
};

type TPlatformCascaderDisplayRenderOptions = {
  /** 全选时数据源分组数量，KPI Trends 为 6，Console Data 仅 Alinea 为 1 */
  fullCheckGroupCount?: number;
  width?: number;
};

const getItemDisplayLabel = (item: any, formatMessage: any): string => {
  if (!item) {
    return '';
  }
  return item.labelKey ? formatMessage({ id: item.labelKey }) : item.label || '';
};

/**
 * KPI Trends / Console Data / MetricsTable 共用的平台级联筛选器展示配置
 */
export const createPlatformCascaderDisplayRenderConfig = (
  options: TPlatformCascaderDisplayRenderOptions = {},
) => {
  const { fullCheckGroupCount = 6, width = 220 } = options;

  return {
    customInputDisplayRender: (prop: any) => {
      const {
        isOne,
        isSingleGroupChecked,
        isGroupFullChecked,
        isShortcutGroupChecked,
        shortcutCheckedGroup,
        checkedGroup,
        isMetricChecked,
        showNum,
        showItem,
      } = prop;
      let shortcutCheckedLength = 0;
      shortcutCheckedGroup?.forEach((groupItem: any) => {
        shortcutCheckedLength += Object.values(groupItem.includes).flat(Infinity).length;
      });
      const { formatMessage } = useIntl();
      const dataSourceItem = getCascaderDataSourceItem(showItem);

      return (
        <div className="dbi-cascader-select-button dbi-select-button">
          <span className="select-button-text">
            {formatMessage({ id: 'dbi_platform' })}
          </span>
          {(isOne && (
            <div className="dbi-cascader-select-button-flex">
              <img
                className="dbi-cascader-common-icon"
                src={getDataSourceIcon(dataSourceItem?.icon)}
              />
              <span>
                {getItemDisplayLabel(dataSourceItem, formatMessage)}
                -
                {getItemDisplayLabel(showItem, formatMessage)}
              </span>
            </div>
          )) ||
            (isShortcutGroupChecked && (
              <div className="dbi-cascader-select-button-flex">
                <span>
                  {showItem.length > 0 && showItem.length === shortcutCheckedLength
                    ? shortcutCheckedGroup?.map((item: any, i: number) => (
                        <span key={item.value}>
                          <img
                            className="dbi-cascader-common-icon"
                            src={getDataSourceIcon(SHORTCUT_ICON_MAP[item?.label])}
                          />
                          {getOptionText(item)}
                          {i < shortcutCheckedGroup.length - 1 ? ',' : ''}
                          &nbsp;&nbsp;
                        </span>
                      ))
                    : showItem.length}
                </span>
              </div>
            )) ||
            (isSingleGroupChecked && (
              <div className="dbi-cascader-select-button-flex">
                <img
                  className="dbi-cascader-common-icon"
                  src={getDataSourceIcon(dataSourceItem?.icon)}
                />
                <span>
                  {getItemDisplayLabel(dataSourceItem, formatMessage)}
                  -
                  {dataSourceItem?.key === 'vginsights'
                    ? formatMessage({
                        id: dataSourceItem.children[0].labelKey,
                      })
                    : formatMessage({ id: 'All' })}
                </span>
              </div>
            )) ||
            (isGroupFullChecked &&
              (checkedGroup.length === fullCheckGroupCount ? (
                <span>{formatMessage({ id: 'All' })}</span>
              ) : (
                <div className="dbi-cascader-select-button-flex">
                  <span>
                    {checkedGroup.map((checkItem: any, index: number) => {
                      if (checkItem.key === 'vginsights') {
                        return (
                          <span key={checkItem?.key} style={{ marginRight: 6 }}>
                            <img
                              className="dbi-cascader-common-icon"
                              src={getDataSourceIcon(checkItem?.icon)}
                            />
                            <span>
                              {checkItem?.labelKey
                                ? formatMessage({ id: checkItem?.labelKey })
                                : checkItem?.label}
                            </span>
                            <span>
                              -
                              {formatMessage({
                                id: checkItem.children[0].labelKey,
                              })}
                              {index < checkedGroup.length - 1 ? ',' : ''}
                            </span>
                          </span>
                        );
                      }
                      return (
                        <span key={checkItem?.key} style={{ marginRight: 6 }}>
                          <img
                            className="dbi-cascader-common-icon"
                            src={getDataSourceIcon(checkItem?.icon)}
                          />
                          <span>
                            {checkItem?.labelKey
                              ? formatMessage({ id: checkItem?.labelKey })
                              : checkItem?.label}
                          </span>
                          <span>
                            -{formatMessage({ id: 'All' })}{' '}
                            {index < checkedGroup.length - 1 ? ',' : ''}
                          </span>
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))) ||
            (isMetricChecked && (
              <div className="dbi-cascader-select-button-txt">{showNum}</div>
            )) || (
              <div className="dbi-cascader-select-button-placeholder">
                {formatMessage({ id: 'dbi_cascader_placeholder' })}
              </div>
            )}
        </div>
      );
    },
    customSearchItemDisplayRender: (prop: any) => {
      const {
        selectedItem,
        formatMessage,
        showLabelFormatFn: getShowLabel,
        tagAutoWidth,
        tagWidth,
        displayParentText,
      } = prop;
      const dataSourceItem = getCascaderDataSourceItem(selectedItem) || {};
      return (
        <span style={{ maxWidth: tagAutoWidth ? 'unset' : tagWidth }}>
          {displayParentText
            ? `${getShowLabel(dataSourceItem, formatMessage)} -`
            : ''}
          <img
            className="dbi-cascader-common-icon"
            style={{ verticalAlign: 'middle' }}
            src={getDataSourceIcon(selectedItem?.icon)}
          />
          {getShowLabel(selectedItem, formatMessage)}
        </span>
      );
    },
    isUseClose: true,
    isNoShortcutClear: true,
    width,
  };
};
